import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

const PARAGRAPHS = [
  "I didn't create The Shift because I had all the answers.",
  "I created it because I started asking better questions.",
];

const QUESTIONS = [
  "Questions about consciousness.",
  "Questions about energy and frequency.",
  "Questions about why we experience life the way we do.",
  "Questions about who we really are beneath everything we were taught to believe.",
];

const REST = [
  "And somewhere along that journey, I realized something:",
];

const BODY = [
  "The Shift was born from that realization.",
  "It is a space for people who feel there is more to life than simply following the same patterns, repeating the same cycles, and living according to someone else's definition of reality.",
  "My purpose is to share the knowledge, ideas, experiences, and perspectives that have helped me look at life differently — and create a place where others can explore their own consciousness without being told what they have to believe.",
  "Here, we explore spirituality, consciousness, energy, frequency, the universe, ancient wisdom, personal transformation, and the possibilities of shifting the way we experience reality.",
  "I believe growth begins when we become willing to question the old programming.",
  "Healing begins when we become willing to look within.",
  "And transformation begins when we decide that the person we have been doesn't have to define the person we become.",
];

const CLOSE = [
  "The Shift isn't about becoming someone else.",
  "It's about remembering who you are.",
  "If something here makes you think, question, heal, grow, or see yourself and your reality differently, then The Shift is doing what I created it to do.",
];

export default function Founder() {
  return (
    <div data-testid="founder-page" className="pt-24 pb-16 sm:pb-24">
      {/* HERO */}
      <section className="relative overflow-hidden aurora grain">
        <div className="absolute inset-0 starfield opacity-30 dark:opacity-60 pointer-events-none" />
        <div className="relative mx-auto max-w-6xl px-5 sm:px-6 py-12 sm:py-20">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-10 md:gap-14 items-center">
            {/* Photo */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="md:col-span-2"
            >
              <div className="relative">
                <div className="absolute -inset-2 bg-accent/10 blur-2xl rounded-sm pointer-events-none" />
                <img
                  src="/images/tay-founder.jpg"
                  alt="Tay — Founder of The Shift"
                  className="relative w-full h-auto object-cover border border-border/60 rounded-sm grayscale-[15%] hover:grayscale-0 transition-all duration-700"
                  data-testid="founder-photo"
                />
              </div>
              <div className="mt-5 space-y-1.5 text-center md:text-left">
                <div className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.32em] text-accent">
                  Tay <span className="text-muted-foreground mx-1">|</span> Founder of The Shift
                </div>
                <div className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.28em] text-muted-foreground">
                  Spirituality • Consciousness • Energy • Frequency • Transformation
                </div>
              </div>
            </motion.div>

            {/* Headline / Quote */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="md:col-span-3"
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-accent/40 text-accent font-mono text-[10px] tracking-[0.32em] uppercase mb-6">
                <Sparkles className="h-3 w-3" /> Meet the Founder
              </div>
              <blockquote className="font-serif font-light text-2xl sm:text-4xl lg:text-5xl leading-[1.15] tracking-tight">
                <span className="text-accent">"</span>
                I created The Shift to help people question the programming, explore consciousness, and discover the{" "}
                <span className="italic text-accent">power within themselves.</span>
                <span className="text-accent">"</span>
              </blockquote>
              <div className="mt-8 font-mono text-[10px] sm:text-xs uppercase tracking-[0.32em] text-muted-foreground">
                — Tay, Founder of The Shift
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* BODY */}
      <section className="mx-auto max-w-3xl px-5 sm:px-6 py-14 sm:py-20">
        <div className="eyebrow mb-4">Meet the Founder of The Shift</div>
        <h2 className="font-serif text-3xl sm:text-5xl leading-tight mb-10">
          Tay <span className="text-muted-foreground">—</span>{" "}
          <span className="italic text-accent">Founder of The Shift</span>
        </h2>

        <div className="space-y-6 font-serif text-lg sm:text-xl leading-[1.7] text-foreground/90">
          {PARAGRAPHS.map((p, i) => (
            <motion.p
              key={`p-${i}`}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              data-testid={`founder-para-${i}`}
            >
              {p}
            </motion.p>
          ))}

          <div className="space-y-1 py-2 border-l-2 border-accent/40 pl-5">
            {QUESTIONS.map((q, i) => (
              <motion.p
                key={`q-${i}`}
                initial={{ opacity: 0, x: -6 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className="text-base sm:text-lg text-muted-foreground italic"
              >
                {q}
              </motion.p>
            ))}
          </div>

          {REST.map((p, i) => (
            <p key={`r-${i}`}>{p}</p>
          ))}

          <p className="font-serif text-xl sm:text-3xl leading-[1.3] text-accent italic py-2">
            The greatest shift doesn't happen outside of you. It happens within you.
          </p>

          {BODY.map((p, i) => (
            <motion.p
              key={`b-${i}`}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.45 }}
            >
              {p}
            </motion.p>
          ))}

          <div className="space-y-2 py-4">
            {CLOSE.map((p, i) => (
              <p key={`c-${i}`}>{p}</p>
            ))}
          </div>

          <p className="font-serif text-xl sm:text-2xl leading-[1.35]">
            Welcome to The Shift.
          </p>
          <p className="italic text-muted-foreground">
            Your reality can change when you change what happens within.
          </p>

          <div className="pt-6 mt-6 border-t border-border/60 space-y-1 font-mono text-xs uppercase tracking-[0.28em] text-muted-foreground">
            <div>— Tay</div>
            <div>Founder, The Shift</div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-14 sm:mt-16 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
          <Link to="/shift-reader" className="flex-1 sm:flex-none">
            <Button
              size="lg"
              className="w-full sm:w-auto btn-pill font-mono tracking-[0.24em] uppercase text-xs h-12 px-8 gap-2"
              data-testid="founder-cta-reader"
            >
              Take the Shift Reader <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
          <Link
            to="/community"
            className="font-mono text-xs uppercase tracking-[0.24em] text-muted-foreground hover:text-foreground border-b border-transparent hover:border-current pb-1 text-center sm:text-left"
            data-testid="founder-cta-community"
          >
            Join the community →
          </Link>
        </div>
      </section>
    </div>
  );
}
